import create from 'zustand';

import APIService from '../../../services/api.service';
import { convertBackendItemToFrontendItem } from '../../../types/item.mapper';
import type { DuplicatePartGroup } from '../components/duplicate-parts/utils/duplicate-parts.utils';
import { findDuplicateParts } from '../components/duplicate-parts/utils/duplicate-parts.utils';
import { useConnectedItemsState } from './connected-items.state';
import { DEFAULT_QUERY } from './table-data.state';

type SucceededRefreshItem = { itemNo: string; message: string };

export type DuplicateWithContext = DuplicatePartGroup & { itemNo: string; key: string; refreshMessage?: string };

interface DuplicatePartsModalState {
   visible: boolean;
   scanning: boolean;
   duplicates: DuplicateWithContext[];
   scanForDuplicates: (items: SucceededRefreshItem[]) => Promise<void>;
   closeModal: () => void;
}

export const useDuplicatePartsModalState = create<DuplicatePartsModalState>((set) => ({
   visible: false,
   scanning: false,
   duplicates: [],

   scanForDuplicates: async (items: SucceededRefreshItem[]) => {
      set({ scanning: true });

      try {
         const itemNos = items.map((item) => item.itemNo);
         const refreshMessages = new Map(items.map((item) => [item.itemNo, item.message]));

         const response = await APIService.getItems({
            ...DEFAULT_QUERY,
            itemNos,
            pageSize: itemNos.length,
            offset: 0,
            progress: false,
         });

         const duplicates = response.items.map(convertBackendItemToFrontendItem).flatMap((item) =>
            findDuplicateParts(item.subItems || []).map((duplicate) => ({
               ...duplicate,
               itemNo: item.itemNo,
               key: `${item.itemNo}::${duplicate.key}`,
               refreshMessage: refreshMessages.get(item.itemNo),
            }))
         );

         if (duplicates.length > 0 && !useConnectedItemsState.getState().hasErrors()) {
            useConnectedItemsState.getState().closeDialogue();
         }

         set({ scanning: false, duplicates, visible: duplicates.length > 0 });
      } catch (error) {
         console.error('Duplicate part scan failed:', error);
         set({ scanning: false });
      }
   },

   closeModal: () => {
      set({ visible: false, duplicates: [] });
   },
}));
